import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";

type OnChangeFunc<T> = (newVal: T) => void;
export const useDebouncedState = <T>({
  delay = 1000,
  ...props
}: {
  value: T;
  onChange: OnChangeFunc<T>;
  delay?: number;
}) => {
  const [internalValue, setInternalValue] = useState(props.value);
  const onChangeRef = useRef<OnChangeFunc<T> | undefined>();
  // Always keep in sync
  onChangeRef.current = props.onChange;

  useLayoutEffect(() => setInternalValue(props.value), [props.value]);

  const debouncedOnChange = useMemo(() => debounce((newVal: T) => onChangeRef.current?.(newVal), delay), [delay]);
  const onChange = useCallback(
    (newVal: T) => {
      setInternalValue(newVal);
      debouncedOnChange(newVal);
    },
    [debouncedOnChange],
  );

  return [internalValue, onChange, debouncedOnChange.flush] as const;
};

export function useDebouncedValue<T>(value: T, setValue: (val: T) => void, delay: number) {
  const [innerValue, setValueCb] = useDebouncedState({
    value,
    onChange: setValue,
    delay,
  });

  return [innerValue, setValueCb] as [T, (val: T) => void];
}

interface UseDelayedUpdateInputProps<T> {
  value: T;
  onChange: (value: T) => void;
}

export function useDelayedUpdateInput<T>({ value, onChange }: UseDelayedUpdateInputProps<T>) {
  const [internalValue, onChangeCb] = useDebouncedState({
    value,
    onChange,
    delay: 500,
  });

  return {
    value: internalValue,
    onChange: onChangeCb,
  };
}
